/**
 * 视频文件前端体检闸(SEEDANCE 真人出镜 P0,0914 车 D5③ → 0915 车 F2 抽到 shared 复用)。
 *
 * 与 ai 侧 B4 入库体检**同份约束**(时长/大小/画幅比例三项):本地当场读、超限即时红字,
 * 不等传完再由后端体检来回一趟。原实现在 `features/characters/ui/CharacterRealCreatePage.tsx`
 * (老板 IP 采集流的口播视频槽);车 F 的「换我出镜」参考视频上传要**复用同一份闸**
 * (任务书 F2:「体检闸复用 checkVideoFile」)——两个 feature(characters/video)都要用,
 * 按 FSD 规矩落 shared 层,不许 features 互相 import。
 *
 * ★fps 前端测不了(浏览器 `<video>` 元数据不暴露帧率,要逐帧采样才能测,成本不值)——
 *   这一项留给服务端 B4 用 ffprobe 兜底复检(分工写在这里,别以为前端漏了)。
 * ★「时长 >15s」与其余项性质不同:B4 对超长视频是**截前 15 秒复用**,不是硬拒
 *   (`超长(>15s)截前15s`,见任务书 B4)——所以这一项前端只**警示**(红字 + 说明会被截),
 *   不阻断提交;太短/太大/比例太极端/分辨率太低这几项 B4 是直接判 `failed`,前端同步**阻断**
 *   (不发这个文件的入库/参考请求),避免白提交一个注定失败的素材。
 * ★0915 老板走查补三项(「体检的闸估计没做好」实锤三洞):
 *   ① 格式:B4 只收 mp4/mov,但此前只有文件选择器的 accept 在拦——**拖拽路不吃 accept**
 *     (专属页换我出镜的 onDrop 直收 dataTransfer.files),webm/avi 一路绿灯到后端才炸。
 *     f.type 可判定错误时当场阻断;type 为空(部分拖拽源)不据此拦,交给元数据读兜底。
 *   ② 总像素:B4 硬约束 41 万~209 万(厂商素材墙)。**手机默认 4K(830 万)此前前端全绿**、
 *     入库必死——现在下限阻断(放大救不回清晰度),上限只警示(ai 侧 B4 已改「降采样救活」;
 *     换我出镜参考不走 B4,超上限可能被上游拒,警示文案对两个消费方都不说谎)。
 *   ③ duration 非有限值(部分流式封装 Infinity/NaN)此前会漏进「仅警示」分支 —— 按读不出阻断。
 */
import { NGINX_MAX_UPLOAD_MB } from "./uploadLimits";

/**
 * 单文件大小上限 = 产品承诺的 200MB(生产边缘 nginx 220m、backend multipart 200MB、ai 侧 B4 的
 * `_VIDEO_MAX_SIZE_BYTES` 200MB,三处同源)。数字收在 shared/lib/uploadLimits.ts,别再写第二份。
 *
 * ★0921:测试机的边缘 nginx 只有 100m(环境没对齐生产),100~200MB 的片在**测试机上**会被 nginx 回 413 ——
 *   那是测试机的配置问题,不照它把产品上限砍半;413 现在有人话兜底(httpClient)。
 */
export const VIDEO_MAX_BYTES = NGINX_MAX_UPLOAD_MB * 1024 * 1024;
export const VIDEO_MIN_SEC = 2;
export const VIDEO_MAX_SEC = 15;
export const VIDEO_MIN_RATIO = 0.4;
export const VIDEO_MAX_RATIO = 2.5;
/** 与 ai 侧 B4 `_VIDEO_MIN_PIXELS/_VIDEO_MAX_PIXELS` 逐字同值(vendor_asset_registrar.py)。 */
export const VIDEO_MIN_PIXELS = 410_000;
export const VIDEO_MAX_PIXELS = 2_090_000;
/** B4 白名单容器同口径(uploadImageFormats.VIDEO_ACCEPT 的 accept 串是它的选择器投影)。 */
const VIDEO_MIME_ALLOW = new Set(["video/mp4", "video/quicktime"]);

export interface VideoIssue {
  text: string;
  /** true = B4 会判 failed 的三项(太短/太大/比例太极端)+ 读不出元数据,提交时跳过这个文件。 */
  blocksSubmit: boolean;
}

/** 本地读视频时长 + 画面宽高(不用等上传,浏览器 `<video>` 元数据事件当场给)。 */
export function readVideoMeta(
  file: File
): Promise<{ duration: number; width: number; height: number } | null> {
  return new Promise((resolve) => {
    const el = document.createElement("video");
    el.preload = "metadata";
    const url = URL.createObjectURL(file);
    const done = (v: { duration: number; width: number; height: number } | null) => {
      URL.revokeObjectURL(url);
      resolve(v);
    };
    el.onloadedmetadata = () =>
      done({ duration: el.duration, width: el.videoWidth, height: el.videoHeight });
    el.onerror = () => done(null);
    el.src = url;
  });
}

/** 体检一份视频文件,超限即返回红字理由(§B4 同份约束);全部达标返回 null。 */
export async function checkVideoFile(f: File): Promise<VideoIssue | null> {
  if (f.type && !VIDEO_MIME_ALLOW.has(f.type)) {
    return {
      text: `不支持的格式(${f.type})—— 仅支持 mp4/mov,请转换后再传`,
      blocksSubmit: true,
    };
  }
  if (f.size > VIDEO_MAX_BYTES) {
    // 上限数字从常量取,别再写第二份(原来这句写死「200MB」,与判据一起漂了一段时间)
    return {
      text: `文件 ${(f.size / 1024 / 1024).toFixed(0)}MB,超过 ${NGINX_MAX_UPLOAD_MB}MB 上限 —— 换一个小一点的文件`,
      blocksSubmit: true,
    };
  }
  const meta = await readVideoMeta(f);
  if (
    !meta ||
    !Number.isFinite(meta.duration) ||
    meta.duration <= 0 ||
    !meta.width ||
    !meta.height
  ) {
    return { text: "读不出这个视频的时长/画面信息 —— 换一个文件试试", blocksSubmit: true };
  }
  // ★阻断项全部查完才轮到警示项(0915 洞④):此前「超长仅警示」提前 return,
  //   会把排在后面的比例阻断吞掉 —— 20 秒超宽视频只得一句"会被截",照常提交,后端必死。
  //   警示不该有掩盖阻断的能力,顺序即语义。
  if (meta.duration < VIDEO_MIN_SEC) {
    return {
      text: `时长仅 ${meta.duration.toFixed(1)} 秒,至少要 ${VIDEO_MIN_SEC} 秒 —— 换一段更长的`,
      blocksSubmit: true,
    };
  }
  const ratio = meta.width / meta.height;
  if (ratio < VIDEO_MIN_RATIO || ratio > VIDEO_MAX_RATIO) {
    return { text: "画面比例太极端(需在 0.4~2.5 之间)—— 换一个文件", blocksSubmit: true };
  }
  const pixels = meta.width * meta.height;
  if (pixels < VIDEO_MIN_PIXELS) {
    return {
      text: `分辨率 ${meta.width}×${meta.height} 太低(总像素不足 41 万)—— 请传更清晰的原片`,
      blocksSubmit: true,
    };
  }
  if (meta.duration > VIDEO_MAX_SEC) {
    return {
      text: `时长 ${meta.duration.toFixed(1)} 秒,超过 ${VIDEO_MAX_SEC} 秒上限(服务器只会取前 ${VIDEO_MAX_SEC} 秒)—— 建议剪到 5~15 秒再传`,
      blocksSubmit: false,
    };
  }
  if (pixels > VIDEO_MAX_PIXELS) {
    return {
      text: `分辨率 ${meta.width}×${meta.height} 超过 209 万像素上限(约 1080P)—— 建议压到 1080P 再传,过大可能被压缩或被拒`,
      blocksSubmit: false,
    };
  }
  return null;
}
